import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { toSlug, fmt } from '../../services/api/services.api';

const BRAND = '#082189';

/* ─── Keyframes ─── */
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

/* ─── Styled ─── */
const Section = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 26px 0 8px;
`;

const Head = styled.div`
  padding: 0 20px 16px;
`;

const H2 = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.4px;
`;

const Sub = styled.p`
  margin: 4px 0 0;
  font-size: 14px;
  color: #6b7280;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 4px 20px 16px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Cell = styled.div`
  flex: 0 0 264px;
  min-width: 264px;
`;

const Card = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.22s cubic-bezier(0.16,1,0.3,1), box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 14px 36px rgba(0,0,0,0.11);
  }
`;

const PhotoBox = styled.div`
  position: relative;
  height: 160px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f3f4f6;
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s cubic-bezier(0.16,1,0.3,1);

  ${Card}:hover & { transform: scale(1.05); }
`;

const Badge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: ${BRAND};
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  padding: 5px 10px;
  border-radius: 9px;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(8, 33, 137, 0.35);
`;


const Body = styled.div`
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled.p`
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 800;
  color: #111827;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BookBtn = styled.button`
  margin-top: 12px;
  width: 100%;
  border: 1.5px solid ${BRAND};
  background: #ffffff;
  color: ${BRAND};
  font-size: 13px;
  font-weight: 800;
  padding: 9px;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.16s, color 0.16s;

  ${Card}:hover & {
    background: ${BRAND};
    color: #ffffff;
  }
`;

const SubTitle = styled.p`
  margin: 0 0 8px;
  font-size: 12.5px;
  color: #6b7280;
`;

const PriceRow = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const PriceLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const Rating = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 800;
  color: #111827;

  b { color: #f59e0b; font-weight: 900; }
`;

const Price = styled.span`
  font-size: 16px;
  font-weight: 800;
  color: ${BRAND};
`;

const Strike = styled.span`
  font-size: 12.5px;
  color: #9ca3af;
  text-decoration: line-through;
`;

const Skel = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s infinite;
  border-radius: 8px;
`;

/* ─── Card ─── */
const CarouselCard = memo(({ svc }) => {
  const navigate = useNavigate();
  const first = Array.isArray(svc?.subServices) ? svc.subServices[0] : null;
  if (!first) return null;

  const price = (first.discountedPrice || 0) + (first.cgst || 0) + (first.sgst || 0);
  const pct = first.discountPercentage;
  const rating = first.averageRating;

  const go = () =>
    navigate(
      `/${toSlug(svc.subCategoryName || 'services')}/${svc.subCategoryID}/${svc.serviceID}?pkg=${first.packageID}`,
    );

  return (
    <Cell>
      <Card onClick={go}>
        <PhotoBox>
          {pct > 0 ? <Badge>{Math.round(pct)}% OFF</Badge> : null}
          {first.imageURL ? (
            <Photo
              src={first.imageURL}
              alt={svc.serviceName}
              loading="lazy"
              onError={(e) => { e.target.style.visibility = 'hidden'; }}
            />
          ) : null}
        </PhotoBox>
        <Body>
          <Title>{svc.serviceName}</Title>
          {svc.subCategoryName ? <SubTitle>{svc.subCategoryName}</SubTitle> : null}
          <PriceRow>
            <PriceLeft>
              <Price>{fmt(price)}</Price>
              {first.actualPrice > first.discountedPrice ? (
                <Strike>{fmt(first.actualPrice)}</Strike>
              ) : null}
            </PriceLeft>
            {rating > 0 ? <Rating><b>★</b> {Number(rating).toFixed(1)}</Rating> : null}
          </PriceRow>
          <BookBtn>Book Now →</BookBtn>
        </Body>
      </Card>
    </Cell>
  );
});

const SkeletonCard = () => (
  <Cell>
    <Card style={{ pointerEvents: 'none', cursor: 'default' }}>
      <Skel style={{ height: 160, borderRadius: 0 }} />
      <Body>
        <Skel style={{ height: 16, marginBottom: 8 }} />
        <Skel style={{ height: 12, width: '60%', marginBottom: 18 }} />
        <Skel style={{ height: 18, width: '40%' }} />
      </Body>
    </Card>
  </Cell>
);

/* ─── Section ─── */
const ServiceCarousel = ({ title, subtitle, items, loading }) => {
  const list = Array.isArray(items) ? items : [];

  if (!loading && list.length === 0) return null;

  return (
    <Section>
      <Head>
        <H2>{title}</H2>
        {subtitle ? <Sub>{subtitle}</Sub> : null}
      </Head>
      <Row>
        {loading
          ? Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
          : list.map((svc, i) => (
              <CarouselCard key={svc?.serviceID ?? i} svc={svc} />
            ))}
      </Row>
    </Section>
  );
};

export default ServiceCarousel;
